import { LocationService } from "@/service/LocationServices";

interface FolderConflictMeta {
  existingId?: string | null;
}

interface FolderConflictError {
  response?: {
    status?: number;
    data?: {
      meta?: FolderConflictMeta;
    };
  };
}

/**
 * Extracts the already-existing folder id from a `400 "Folder already exists"`
 * response. The Location API returns the conflicting folder id in
 * `meta.existingId`, which lets us recover instead of treating the 400 as a
 * hard failure.
 */
function getExistingFolderIdFromError(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  const { response } = error as FolderConflictError;
  if (response?.status !== 400) {
    return undefined;
  }
  // Only trust a non-empty string id. The 400 "Folder already exists" response
  // is the one path that carries meta.existingId; an empty/null/non-string
  // value means we have nothing safe to reuse, so fall through to a re-throw.
  const existingId = response.data?.meta?.existingId;
  return typeof existingId === "string" && existingId.length > 0
    ? existingId
    : undefined;
}

/**
 * Resolves the custom-field parent folder id for a builder.
 *
 * Creates the folder via the Location API and returns its id. When the folder
 * already exists on the backend the API responds with
 * `400 "Folder already exists"` and includes the existing folder id in
 * `meta.existingId`; we treat that as a successful resolution and reuse the id.
 *
 * This is the deterministic failure described in RCA section 4.3: the backend
 * folder is created the instant a custom field is dropped, but the form's
 * `parentFolderId` is only persisted on save. Reopening a form that was never
 * saved resets `parentFolderId` to "", so the next drop re-fires the create and
 * the backend correctly answers 400. Recovering the id here keeps the drop
 * working instead of silently dropping the field from the canvas.
 *
 * Always resolves to a non-empty folder id. A successful response with a
 * missing id (unexpected API shape / contract drift) throws rather than
 * silently returning `undefined` and writing a bad `parentId` onto the field.
 * Any other error is re-thrown so the caller can surface it.
 */
export async function resolveParentFolderId(
  locationId: string,
  folderName: string
): Promise<string> {
  try {
    const response = await LocationService.createCustomField(locationId, {
      name: folderName,
      documentType: "folder",
      model: "contact",
    });
    const id = response.data?.customFieldFolder?.id;
    if (typeof id !== "string" || id.length === 0) {
      throw new Error(
        `createCustomField succeeded but returned no folder id for "${folderName}"`
      );
    }
    return id;
  } catch (error: unknown) {
    const existingId = getExistingFolderIdFromError(error);
    if (existingId) {
      return existingId;
    }
    throw error;
  }
}
